import React, { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';

const PHASE_ORDER = ['connecting', 'connected', 'developing', 'developed', 'launching', 'running', 'ready'];

export const AiTypingIndicator = () => (
  <span className="ai-typing-indicator" aria-hidden="true">
    <span className="ai-typing-indicator__glow" />
    <span className="ai-typing-indicator__ring ai-typing-indicator__ring--outer" />
    <span className="ai-typing-indicator__ring ai-typing-indicator__ring--inner" />
    <span className="ai-typing-indicator__core" />
  </span>
);

export const getStreamingCodePreviewLines = (text) => {
  if (!text?.trim()) {
    return [];
  }

  const cleaned = text.replace(/```[\w-]*\n?/g, '').replace(/\r/g, '');
  const width = 64;
  const visual = [];

  cleaned.split('\n').forEach((raw) => {
    const line = raw.replace(/^\s*[-*>]+\s+/, '').trimEnd();
    if (!line.trim() || /^#{1,6}\s/.test(line.trim())) {
      return;
    }

    if (line.length <= width) {
      visual.push(line);
      return;
    }

    for (let index = 0; index < line.length; index += width) {
      visual.push(line.slice(index, index + width));
    }
  });

  return visual.slice(-3);
};

const padPreviewLines = (lines) => {
  if (!lines.length) {
    return [];
  }
  if (lines.length === 1) {
    return ['', lines[0], ''];
  }
  if (lines.length === 2) {
    return [lines[0], lines[1], ''];
  }
  return lines.slice(-3);
};

const buildVisibleSteps = (phase, t, includeSandbox) => {
  const index = PHASE_ORDER.indexOf(phase);
  if (index < 0) {
    return [];
  }

  const steps = [
    {
      id: 'connect',
      label: index <= PHASE_ORDER.indexOf('connecting') ? t('llmProgressConnecting') : t('llmProgressConnected'),
      active: phase === 'connecting',
      done: index >= PHASE_ORDER.indexOf('connected')
    }
  ];

  if (index >= PHASE_ORDER.indexOf('connected')) {
    steps.push({
      id: 'develop',
      label: index <= PHASE_ORDER.indexOf('developing') ? t('llmProgressDeveloping') : t('llmProgressDeveloped'),
      active: phase === 'connected' || phase === 'developing',
      done: index >= PHASE_ORDER.indexOf('developed'),
      showCode: phase === 'developing'
    });
  }

  if (includeSandbox && index >= PHASE_ORDER.indexOf('launching')) {
    steps.push({
      id: 'launch',
      label: t('llmProgressLaunching'),
      active: phase === 'launching',
      done: index >= PHASE_ORDER.indexOf('running')
    });
  }

  if (includeSandbox && index >= PHASE_ORDER.indexOf('running')) {
    steps.push({
      id: 'run',
      label: t('llmProgressRunning'),
      active: phase === 'running',
      done: index >= PHASE_ORDER.indexOf('ready')
    });
  }

  if (index >= PHASE_ORDER.indexOf('ready')) {
    steps.push({
      id: 'ready',
      label: t('llmProgressReady'),
      active: phase === 'ready',
      done: true
    });
  }

  return steps;
};

const CodePreview = ({ lines }) => {
  const padded = padPreviewLines(lines);

  if (!padded.length) {
    return null;
  }

  return (
    <div className="llm-code-preview mt-2 overflow-hidden rounded-2xl bg-neutral-100 px-3 py-2.5 dark:bg-neutral-900">
      {padded.map((line, index) => (
        <div
          key={index}
          className={classNames(
            'flex min-w-0 items-center gap-0.5 font-mono text-[11px] leading-5 tracking-tight text-neutral-700 transition-[opacity,filter] duration-500 ease-out dark:text-neutral-300',
            index === 1 ? 'opacity-100' : 'opacity-30 blur-[0.4px]'
          )}
        >
          <span className="llm-code-preview__line min-w-0 flex-1">
            <span className="llm-code-preview__text" dir="ltr">
              {line.trim() ? line : '\u00a0'}
            </span>
          </span>
          {index === 1 ? <span className="llm-code-caret shrink-0" aria-hidden /> : null}
        </div>
      ))}
    </div>
  );
};

export const LlmGenerationProgress = ({ phase, includeSandbox = false, streamText = '', className }) => {
  const { t } = useI18n();
  const steps = useMemo(
    () => buildVisibleSteps(phase, t, includeSandbox),
    [includeSandbox, phase, t]
  );
  const previewLines = useMemo(() => getStreamingCodePreviewLines(streamText), [streamText]);

  if (!steps.length) {
    return null;
  }

  return (
    <div className={classNames('w-full max-w-md', className)} aria-live="polite">
      <ol className="flex flex-col gap-2.5">
        <AnimatePresence initial={false}>
          {steps.map((step) => (
            <motion.li
              key={step.id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex h-[1.35rem] w-[1.35rem] shrink-0 items-center justify-center">
                  {step.active && !step.done ? (
                    <AiTypingIndicator />
                  ) : (
                    <span
                      className={classNames(
                        'inline-flex h-4 w-4 items-center justify-center rounded-full',
                        step.done
                          ? 'bg-neutral-200 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400'
                          : 'bg-neutral-100 text-neutral-300 dark:bg-neutral-900'
                      )}
                    >
                      <Check size={10} strokeWidth={3} />
                    </span>
                  )}
                </span>
                <span
                  className={classNames(
                    'text-sm transition-colors duration-300',
                    step.active && !step.done
                      ? 'font-medium text-neutral-800 dark:text-neutral-100'
                      : 'text-neutral-400'
                  )}
                >
                  {step.label}
                </span>
              </div>
              {step.showCode ? (
                <div className="ms-[calc(1.35rem+0.625rem)]">
                  <CodePreview lines={previewLines} />
                </div>
              ) : null}
            </motion.li>
          ))}
        </AnimatePresence>
      </ol>
    </div>
  );
};
